import React from 'react';
import {
  type TimeScale,
  type TimeInterval,
  dateToPixel,
  getPixelsPerUnit,
} from './ganttTimeUtils';
import { pmTheme } from './pmTheme';

interface GanttTimeHeaderProps {
  startDate: Date;
  endDate: Date;
  timeScale: TimeScale;
  intervals: TimeInterval[];
  showTodayLine: boolean;
  showGridLines: boolean;
  showIterationBands: boolean;
  iterations?: { name: string; startDate: Date; endDate: Date }[];
  totalWidth: number;
  headerHeight: number;
  bodyHeight: number;
  todayLineColor?: string;
}

export const GanttTimeHeader: React.FC<GanttTimeHeaderProps> = ({
  startDate,
  timeScale,
  intervals,
  showTodayLine,
  showGridLines,
  showIterationBands,
  iterations,
  totalWidth,
  headerHeight,
  bodyHeight,
  todayLineColor = '#EF4444',
}) => {
  const ppu = getPixelsPerUnit(timeScale);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayX = dateToPixel(today, timeScale, startDate, ppu);

  // Generate secondary header labels (month names for day/week scales)
  const monthHeaders: { label: string; x: number; width: number }[] = [];
  if (timeScale === 'day' || timeScale === 'week') {
    let currentMonth = -1;
    let monthStartX = 0;
    for (const interval of intervals) {
      const m = interval.date.getMonth();
      const y = interval.date.getFullYear();
      const key = y * 12 + m;
      const ix = dateToPixel(interval.date, timeScale, startDate, ppu);
      if (key !== currentMonth) {
        if (currentMonth !== -1) {
          monthHeaders[monthHeaders.length - 1].width = ix - monthStartX;
        }
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'];
        monthHeaders.push({ label: `${monthNames[m]} ${y}`, x: ix, width: 0 });
        monthStartX = ix;
        currentMonth = key;
      }
    }
    if (monthHeaders.length > 0) {
      monthHeaders[monthHeaders.length - 1].width = totalWidth - monthHeaders[monthHeaders.length - 1].x;
    }
  }

  const secondaryHeaderHeight = (timeScale === 'day' || timeScale === 'week') ? 20 : 0;

  return (
    <g data-testid="gantt-time-header">
      {/* Header background */}
      <rect x={0} y={0} width={totalWidth} height={headerHeight} fill={pmTheme.surfaceSubtle} />
      <line x1={0} y1={headerHeight} x2={totalWidth} y2={headerHeight} stroke={pmTheme.border} strokeWidth={1} />

      {/* Secondary header (month names) */}
      {monthHeaders.map((mh, i) => (
        <g key={`month-${i}`}>
          <text
            x={mh.x + 4}
            y={12}
            fontSize={10}
            fill={pmTheme.textMuted}
            fontWeight={600}
          >
            {mh.label}
          </text>
          <line x1={mh.x} y1={0} x2={mh.x} y2={secondaryHeaderHeight} stroke={pmTheme.border} strokeWidth={0.5} />
        </g>
      ))}

      {/* Primary interval labels */}
      {intervals.map((interval, i) => {
        const ix = dateToPixel(interval.date, timeScale, startDate, ppu);
        const isWeekend = timeScale === 'day' && (interval.date.getDay() === 0 || interval.date.getDay() === 6);
        return (
          <g key={`interval-${i}`}>
            <text
              x={ix + 4}
              y={secondaryHeaderHeight + 14}
              fontSize={10}
              fill={isWeekend ? pmTheme.textSubtle : pmTheme.text}
              data-testid={`gantt-interval-${i}`}
            >
              {interval.label}
            </text>
            {/* Grid lines */}
            {showGridLines && (
              <line
                x1={ix}
                y1={headerHeight}
                x2={ix}
                y2={headerHeight + bodyHeight}
                stroke={isWeekend ? pmTheme.borderSubtle : pmTheme.border}
                strokeWidth={0.5}
                strokeDasharray={isWeekend ? '2,2' : undefined}
              />
            )}
            {/* Weekend shading */}
            {isWeekend && timeScale === 'day' && (
              <rect
                x={ix}
                y={headerHeight}
                width={ppu}
                height={bodyHeight}
                fill={pmTheme.surfaceSubtle}
                opacity={0.5}
              />
            )}
          </g>
        );
      })}

      {/* Iteration/sprint bands */}
      {showIterationBands && iterations?.map((iter, i) => {
        const iterStartX = dateToPixel(iter.startDate, timeScale, startDate, ppu);
        const iterEndX = dateToPixel(iter.endDate, timeScale, startDate, ppu);
        const colors = ['#EFF6FF', '#FEF3C7', '#F0FDF4', '#FDF2F8'];
        return (
          <g key={`iter-${i}`}>
            <rect
              x={iterStartX}
              y={headerHeight}
              width={iterEndX - iterStartX}
              height={bodyHeight}
              fill={colors[i % colors.length]}
              opacity={0.3}
              data-testid={`gantt-iteration-band-${i}`}
            />
            <text
              x={iterStartX + 4}
              y={headerHeight + 12}
              fontSize={9}
              fill={pmTheme.textMuted}
              fontWeight={500}
              opacity={0.7}
            >
              {iter.name}
            </text>
          </g>
        );
      })}

      {/* Today line */}
      {showTodayLine && todayX > 0 && (
        <g data-testid="gantt-today-line">
          <line
            x1={todayX}
            y1={0}
            x2={todayX}
            y2={headerHeight + bodyHeight}
            stroke={todayLineColor}
            strokeWidth={2}
            strokeDasharray="4,2"
          />
          <text
            x={todayX + 4}
            y={headerHeight - 4}
            fontSize={9}
            fill={todayLineColor}
            fontWeight={600}
          >
            Today
          </text>
        </g>
      )}
    </g>
  );
};

GanttTimeHeader.displayName = 'GanttTimeHeader';
